import { FC, Fragment, useState } from 'react';
// Redux
import { useAppDispatch } from 'app/hooks/useStore';
// Models
import { ISubscription } from 'app/store/Subscriptions/Subscriptions.models';
// Types
import { SubscriptionStatus } from 'app/store/Subscriptions/Subscriptions.types';
// Async
import { cancelSubscription } from 'app/store/BillingSubscriptions/BillingSubscriptions.async';
// Mui
import {
  Theme,
  Paper, Box, Typography, Divider,
  Table, TableRow, TableCell, TableBody,
  TableHead
} from '@mui/material';
// Icons
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
// Components
import { InfoBlock } from 'app/components/Utilities';
import { IconButton } from 'app/components/Mui';
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
// Hooks
import useLongDateTime from 'app/hooks/useLongDateTime';
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';
// Utils
import { toNumberWithCommas, statusThemeColor } from 'app/utilities/Utilities';
// i18next
import { Trans, useTranslation } from 'react-i18next';

type Props = {
  subscription: ISubscription;
}

const SubscriptionItem:FC<Props> = ({
  // Props
  subscription
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { subscriptionPackage } = subscription;

  const [ loading, setLoading ] = useState(false);
  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();

  const [createdOn] = useLongDateTime({
    dateValue: subscription.createdOn,
    local: true
  });

  const [currentPeriodEnd] = useLongDateTime({
    dateValue: subscription.currentPeriodEnd,
    local: true
  });

  const [trialPeriodEnd] = useLongDateTime({
    dateValue: subscription.trialPeriodEnd,
    local: true
  });

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await dispatch(cancelSubscription(subscription.id)).unwrap();
      toggle();
    } finally {
      setLoading(false);
    }
  }

  const showDropdownMenu = [SubscriptionStatus.Active, SubscriptionStatus.Trialing].includes(subscription.status);
  const isTrialing = subscription.status === SubscriptionStatus.Trialing;

  return (
    <Fragment>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography
              sx={{ flexGrow: 1 }}
              variant="subtitle1"
            >{subscriptionPackage.name}</Typography>
            <Typography
              variant="body2"
              sx={{
                textTransform: 'capitalize',
                color: (theme:Theme) => statusThemeColor(subscription.status, theme)
              }}
            >{subscription.status}</Typography>
          </Box>
          {showDropdownMenu ? (
            <Box>
              <IconButton
                name={`Subscription dropdown ${subscription.id}`}
                size="small"
                aria-label="Subscription dropdown button"
                onClick={openMenu}
              ><MoreVertIcon /></IconButton>
              <Menu>
                <MenuItem
                  name={`Cancel subscription ${subscription.id}`}
                  sx={{
                    color: (theme:Theme) => theme.palette.error.main
                  }}
                  onClick={toggle}
                >{t('buttons.cancel')}</MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Box>
        {subscriptionPackage.description ? (
          <Typography color="CaptionText" variant="body2">{subscriptionPackage.description}</Typography>
        ) : null}
        {createdOn || currentPeriodEnd || (trialPeriodEnd && isTrialing) ? (
          <Fragment>
            <Divider sx={{ borderColor: (theme:Theme) => theme.palette.divider, my: 4 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              {createdOn ? (
                <InfoBlock label={t('labels.createdOn')} value={createdOn} />
              ) : null}
              {currentPeriodEnd ? (
                <InfoBlock label={t('labels.nextBillDate')} value={currentPeriodEnd} />
              ) : null}
              {trialPeriodEnd && isTrialing ? (
                <InfoBlock label={t('labels.trialEndsOn')} value={trialPeriodEnd} />
              ) : null}
            </Box>
          </Fragment>
        ) : null}
        {subscription.items && subscription.items.length !== 0 ? (
          <Table sx={{
            // bgcolor: (theme:Theme) => theme.palette.primary.light,
            // border: (theme:Theme) => `1px solid ${theme.palette.divider}`,
            mt: 4
          }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('labels.name')}</TableCell>
                <TableCell sx={{ width: '20%' }} align="right">{t('labels.qty')}</TableCell>
                <TableCell sx={{ width: '20%' }} align="right">{t('labels.price')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscription.items.map((subscriptionItem, index) => (
                <TableRow key={`subscriptionItem-${index}-${subscription.id}`}>
                  <TableCell>{subscriptionItem.service.name}</TableCell>
                  <TableCell align="right">{typeof subscriptionItem.quantity === 'undefined' ? '-' : subscriptionItem.quantity}</TableCell>
                  <TableCell align="right">{`$${toNumberWithCommas(subscriptionItem.price)}`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </Paper>

      {open ? (
        <ConfirmationDialog
          open={open}
          onClose={toggle}
          onConfirm={handleConfirm}
          title={t('pages.subscriptions.cancelTitle')}
          content={(
            <Trans
              t={t} i18nKey="pages.subscriptions.cancelConfirmation"
              components={{ strong: <strong /> }}
              values={{ subscriptionName: subscriptionPackage.name }}
            />
          )}
          loading={loading}
        />
      ) : null}
    </Fragment>
  )
}

export default SubscriptionItem;